// The FIRST-PERSON PLAYER.
//
// One class, PlayerController, owning everything between the input devices and
// the camera transform: look (pointer lock, mouse drag when unlocked, touch
// drag), WASD movement with run and crouch, head bob, a small lean toward
// whatever the interaction layer points at, and the collision resolve.
//
// The resolve is two passes. `_resolve` decides which box the player is in —
// one of the ALCOVES cut into the side walls, or the main room — and clamps to
// that box, allowing passage through the alcove's opening only while the target
// Z lies inside `openingW`. `_resolveFixtures` then pushes the point out of any
// COLLIDERS AABB along whichever of the four inflated faces is nearest.
//
// `leanTarget` is written from outside: CrateInteractor sets it to the hit point
// under the crosshair and clears it to null.
//
// Recovered from the production bundle. Numbers and strings are byte-identical
// to the shipped source.

import * as THREE from 'three';
import { ROOM_HALF_W, ALCOVES } from './zones.js';
import { ROOM } from './room.js';
import { COLLIDERS } from './store.js';

const EYE_HEIGHT = 1.66;
const EYE_HEIGHT_CROUCH = 1.15;
const PLAYER_RADIUS = .26;
// Metres per second. Shift runs. RUN_SPEED was 2.35, under twice the walk, which
// made crossing the 16.5 m shop a seven-second plod; it is now about 3.3x walking.
const WALK_SPEED = 1.28;
const RUN_SPEED = 4.2;
const CROUCH_SPEED = .7;
// Movement is split into steps no longer than this before collision is resolved, so
// a long frame (the loop clamps dt at 0.05 s, which is 0.21 m at run speed) cannot
// carry the player through a thin collider in one step.
const MAX_STEP = .1;
// Head-bob cadence follows speed, but stops following it here: the old run speed.
// Left uncapped, a 4.2 m/s run would shake the camera vertically at about 7 Hz.
const BOB_SPEED_CAP = 2.35;

class PlayerController {
    constructor(camera, dom) {
        this.camera = camera;
        this.dom = dom;
        this.yaw = Math.PI;
        this.pitch = 0;

        // Spawn inside the entrance alcove, facing out through its opening.
        const entrance = ALCOVES.find((zone) => zone.entrance);
        if (entrance) {
            const alcove = entrance.alcove;
            const spawnX = alcove.side * (ROOM_HALF_W + alcove.w / 2) + alcove.side * (alcove.w / 2 - .95);
            this.pos = new THREE.Vector3(spawnX, EYE_HEIGHT, alcove.z);
            this.yaw = alcove.side > 0 ? Math.PI / 2 : -Math.PI / 2;
        } else {
            this.pos = new THREE.Vector3(0, EYE_HEIGHT, ROOM.d / 2 - 1.6);
        }

        this.vel = new THREE.Vector3();
        this.keys = new Set();
        this.locked = false;
        this.bob = 0;
        this.bobAmt = 0;
        this.eye = EYE_HEIGHT;
        this.crouch = 0;
        this.leanTarget = null;
        this.lean = new THREE.Vector3();
        this._onMove = this._onMove.bind(this);

        dom.addEventListener("click", () => {
            if (!this.locked) dom.requestPointerLock();
        });
        document.addEventListener("pointerlockchange", () => {
            this.locked = document.pointerLockElement === dom;
            if (this.locked) this.dragging = false;
        });

        this.dragging = false;

        dom.addEventListener("mousedown", (event) => {
            if (!this.locked && event.button === 0) {
                this.dragging = true;
                event.preventDefault();
            }
        });
        addEventListener("mouseup", () => {
            this.dragging = false;
        });
        addEventListener("blur", () => {
            this.dragging = false;
        });

        let lastTouchX = 0;
        let lastTouchY = 0;
        dom.addEventListener("touchstart", (event) => {
            const touch = event.touches[0];
            lastTouchX = touch.clientX;
            lastTouchY = touch.clientY;
        }, {
            passive: true
        });
        dom.addEventListener("touchmove", (event) => {
            const touch = event.touches[0];
            this._look(touch.clientX - lastTouchX, touch.clientY - lastTouchY);
            lastTouchX = touch.clientX;
            lastTouchY = touch.clientY;
        }, {
            passive: true
        });

        let lastDragX = 0;
        let lastDragY = 0;
        let haveDragOrigin = false;
        document.addEventListener("mousemove", (event) => {
            if (this.locked) {
                this._onMove(event);
                haveDragOrigin = false;
                return;
            }
            if (!this.dragging) {
                haveDragOrigin = false;
                return;
            }
            // The first move after a drag starts only seeds the origin.
            if (haveDragOrigin) this._look(event.clientX - lastDragX, event.clientY - lastDragY);
            lastDragX = event.clientX;
            lastDragY = event.clientY;
            haveDragOrigin = true;
        });

        addEventListener("keydown", (event) => {
            this.keys.add(event.code);
            if (["Space", "ArrowUp", "ArrowDown"].includes(event.code)) event.preventDefault();
        });
        addEventListener("keyup", (event) => this.keys.delete(event.code));
        addEventListener("blur", () => this.keys.clear());
    }

    _onMove(event) {
        if (!this.locked && !this.dragging) return;
        this._look(event.movementX ?? 0, event.movementY ?? 0);
    }

    _look(dx, dy) {
        this.yaw -= dx * .0021;
        this.pitch -= dy * .0021;
        const limit = Math.PI / 2 - .06;
        this.pitch = Math.max(-limit, Math.min(limit, this.pitch));
    }

    _resolve(target) {
        const radius = PLAYER_RADIUS;
        for (const zone of ALCOVES) {
            const alcove = zone.alcove;
            const centerX = alcove.side * (ROOM_HALF_W + alcove.w / 2);
            const pastWallLine = (x) => alcove.side < 0 ? x < -ROOM_HALF_W + radius : x > ROOM_HALF_W - radius;
            const insideNow = pastWallLine(this.pos.x) && Math.abs(this.pos.z - alcove.z) < alcove.d / 2;
            const targetPastWall = pastWallLine(target.x);
            if (!insideNow && !targetPastWall) continue;
            const inOpening = Math.abs(target.z - alcove.z) < alcove.openingW / 2 - radius * .5;
            if (!insideNow && !inOpening) continue;
            const withinDepth = Math.abs(target.z - alcove.z) < alcove.d / 2 - radius;
            if (!inOpening && !withinDepth && !insideNow) continue;
            const wallX = alcove.side * ROOM_HALF_W;
            if (alcove.side < 0) {
                target.x = Math.max(centerX - alcove.w / 2 + radius, target.x);
                if (!inOpening) target.x = Math.min(wallX - radius, target.x);
            } else {
                target.x = Math.min(centerX + alcove.w / 2 - radius, target.x);
                if (!inOpening) target.x = Math.max(wallX + radius, target.x);
            }
            target.z = Math.max(alcove.z - alcove.d / 2 + radius, Math.min(alcove.z + alcove.d / 2 - radius, target.z));
            return this._resolveFixtures(target);
        }
        target.x = Math.max(-ROOM.w / 2 + radius, Math.min(ROOM.w / 2 - radius, target.x));
        target.z = Math.max(-ROOM.d / 2 + radius, Math.min(ROOM.d / 2 - radius, target.z));
        return this._resolveFixtures(target);
    }

    _resolveFixtures(target) {
        const radius = PLAYER_RADIUS;
        for (const box of COLLIDERS)
            if (target.x > box.minX - radius && target.x < box.maxX + radius && target.z > box.minZ - radius && target.z < box.maxZ + radius) {
                // Push out along whichever inflated face is nearest.
                const dMinX = Math.abs(target.x - (box.minX - radius));
                const dMaxX = Math.abs(target.x - (box.maxX + radius));
                const dMinZ = Math.abs(target.z - (box.minZ - radius));
                const dMaxZ = Math.abs(target.z - (box.maxZ + radius));
                const nearest = Math.min(dMinX, dMaxX, dMinZ, dMaxZ);
                if (nearest === dMinX) target.x = box.minX - radius;
                else if (nearest === dMaxX) target.x = box.maxX + radius;
                else if (nearest === dMinZ) target.z = box.minZ - radius;
                else target.z = box.maxZ + radius;
            }
        return target;
    }

    update(dt) {
        const forward = (this.keys.has("KeyW") ? 1 : 0) - (this.keys.has("KeyS") ? 1 : 0);
        const strafe = (this.keys.has("KeyD") ? 1 : 0) - (this.keys.has("KeyA") ? 1 : 0);
        const running = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight");
        const crouching = this.keys.has("ControlLeft") || this.keys.has("ControlRight");

        this.crouch += ((crouching ? 1 : 0) - this.crouch) * Math.min(1, dt / .22);
        this.eye = EYE_HEIGHT + (EYE_HEIGHT_CROUCH - EYE_HEIGHT) * this.crouch;

        const speed = crouching ? CROUCH_SPEED : running ? RUN_SPEED : WALK_SPEED;
        const moveDir = new THREE.Vector3(strafe, 0, -forward);
        if (moveDir.lengthSq() > 0) moveDir.normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);
        // multiplyScalar is in place: targetVel and moveDir are the same vector.
        const targetVel = moveDir.multiplyScalar(speed);
        this.vel.lerp(targetVel, 1 - Math.pow(.0016, dt));

        // Resolve in steps of at most MAX_STEP. One step at normal frame rates, so
        // walking behaves exactly as before.
        const steps = Math.max(1, Math.ceil(this.vel.length() * dt / MAX_STEP));
        for (let i = 0; i < steps; i++) {
            const next = this.pos.clone().addScaledVector(this.vel, dt / steps);
            this._resolve(next);
            this.pos.copy(next);
        }

        const speedNow = this.vel.length();
        this.bobAmt += (speedNow / speed - this.bobAmt) * Math.min(1, dt * 7);
        this.bob += dt * Math.min(speedNow, BOB_SPEED_CAP) * 5.2;

        const bobY = Math.sin(this.bob * 2) * .019 * this.bobAmt;
        const bobX = Math.cos(this.bob) * .014 * this.bobAmt;
        const leanGoal = this._v0 || (this._v0 = new THREE.Vector3());

        leanGoal.set(0, 0, 0);
        if (this.leanTarget && moveDir.lengthSq() > 0) {
            leanGoal.copy(this.leanTarget).sub(this.camera.position);
            const dist = leanGoal.length();
            if (dist > .001) leanGoal.multiplyScalar(Math.min(.12, dist * .5) / dist);
            else leanGoal.set(0, 0, 0);
        }

        this.lean.lerp(leanGoal, 1 - Math.pow(.004, dt));
        this.camera.position.set(this.pos.x + bobX + this.lean.x, this.eye + bobY + this.lean.y, this.pos.z + this.lean.z);
        this.camera.rotation.set(this.pitch, this.yaw, Math.cos(this.bob) * .006 * this.bobAmt, "YXZ");
    }
}

export { EYE_HEIGHT, PlayerController };
